import React, { useEffect, useState, useCallback } from 'react';
import { X, Pin } from 'lucide-react';
import { ScreenShell } from './AssistantScreens';

export default function AnnouncementsScreen({ cardId, token, api, tx, onBack, surface }) {
    const [msgs, setMsgs] = useState(null);
    const [lightbox, setLightbox] = useState(null);
    const [acking, setAcking] = useState(null);
    const markedRef = React.useRef(new Set());
    const base = api || (typeof window !== 'undefined' ? window.location.origin : '');

    const load = useCallback(() => {
        fetch(`${base}/api/assistant/${cardId}/group-messages?token=${encodeURIComponent(token)}`)
            .then((r) => (r.ok ? r.json() : { messages: [] }))
            .then((d) => {
                const list = d.messages || [];
                setMsgs(list);
                // Mark as OPENED only the ones now shown on this screen (not yet marked).
                const fresh = list.map((m) => m.id).filter((id) => !markedRef.current.has(id));
                if (fresh.length) {
                    fresh.forEach((id) => markedRef.current.add(id));
                    fetch(`${base}/api/assistant/${cardId}/group-messages/read?token=${encodeURIComponent(token)}`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ ids: fresh }),
                    }).catch(() => {});
                }
            })
            .catch(() => setMsgs((prev) => prev || []));
    }, [base, cardId, token]);

    useEffect(() => {
        load();
        const iv = setInterval(load, 5000);
        return () => clearInterval(iv);
    }, [load]);

    const acknowledge = async (id) => {
        setAcking(id);
        try {
            await fetch(`${base}/api/assistant/${cardId}/group-messages/${id}/ack?token=${encodeURIComponent(token)}`, { method: 'POST' });
            setMsgs((prev) => (prev || []).map((m) => (m.id === id ? { ...m, acknowledged: true } : m)));
        } catch (e) { /* noop */ }
        finally { setAcking(null); }
    };

    const scopeLabel = (m) => (m.scope_type === 'floor'
        ? (tx.annFloor || 'Floor {n}').replace('{n}', m.scope_value)
        : (tx.annAll || 'All Residents'));

    const photoUrl = (pid) => `${base}/api/assistant/${cardId}/group-message-photo/${pid}?token=${encodeURIComponent(token)}`;

    // Pinned & not-yet-acknowledged float to the top (required attention); the
    // rest follow in newest-first order.
    const ordered = React.useMemo(() => {
        const list = [...(msgs || [])].reverse();
        const requires = (m) => m.pinned && !m.acknowledged;
        return [...list.filter(requires), ...list.filter((m) => !requires(m))];
    }, [msgs]);

    return (
        <ScreenShell title={tx.annTitle || 'Announcements'} onBack={onBack} tx={tx} testId="announcements-shell">
            <div className="space-y-3" data-testid="announcements-screen">
                {msgs === null ? null : msgs.length === 0 ? (
                    <p className="text-sm text-slate-500" data-testid="announcements-empty">{tx.annEmpty || 'No announcements yet.'}</p>
                ) : (
                    ordered.map((m) => {
                        const pinnedActive = m.pinned && !m.acknowledged;
                        return (
                        <div key={m.id} data-testid={`announcement-${m.id}`}
                            className={`rounded-xl border p-3 ${pinnedActive
                                ? 'border-amber-400 dark:border-amber-500/60 bg-amber-50 dark:bg-amber-500/10 ring-1 ring-amber-300/60'
                                : 'border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-800/60'}`}>
                            <div className="flex items-center justify-between gap-2 mb-1">
                                <span className="text-xs font-semibold text-purple-600 dark:text-purple-300 inline-flex items-center gap-1.5">
                                    {pinnedActive && (
                                        <span className="inline-flex items-center gap-0.5 text-amber-600 dark:text-amber-300" data-testid={`announcement-pinned-${m.id}`}>
                                            <Pin className="w-3 h-3 fill-current" />{tx.annPinned || 'Pinned'}
                                        </span>
                                    )}
                                    {scopeLabel(m)}
                                </span>
                                <span className="text-[11px] text-slate-400">{new Date(m.created_at).toLocaleString()}</span>
                            </div>
                            <p className="text-sm text-slate-800 dark:text-slate-100 whitespace-pre-wrap">{m.body}</p>
                            {m.photo_ids?.length > 0 && (
                                <div className="grid grid-cols-3 gap-2 mt-2" data-testid={`announcement-photos-${m.id}`}>
                                    {m.photo_ids.map((pid) => (
                                        <button key={pid} type="button" onClick={() => setLightbox(photoUrl(pid))}
                                            className="aspect-square rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700"
                                            data-testid={`announcement-photo-${pid}`}>
                                            <img src={photoUrl(pid)} alt="" loading="lazy" className="w-full h-full object-cover" />
                                        </button>
                                    ))}
                                </div>
                            )}
                            <div className="flex items-center justify-between gap-2 mt-2">
                                <span className="text-[11px] text-slate-400">{tx.annFrom || 'From management'}</span>
                                {pinnedActive && (
                                    <button type="button" onClick={() => acknowledge(m.id)} disabled={acking === m.id}
                                        data-testid={`announcement-ack-${m.id}`}
                                        className="text-xs font-semibold rounded-full px-3 py-1.5 bg-amber-500 hover:bg-amber-400 text-white disabled:opacity-50">
                                        {tx.annAck || 'Acknowledge'}
                                    </button>
                                )}
                            </div>
                        </div>
                        );
                    })
                )}
            </div>
            {lightbox && (
                <div onClick={() => setLightbox(null)} data-testid="announcement-lightbox"
                    className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4">
                    <button type="button" onClick={() => setLightbox(null)} data-testid="announcement-lightbox-close"
                        className="absolute top-4 right-4 w-9 h-9 rounded-full bg-black/70 text-white flex items-center justify-center">
                        <X className="w-5 h-5" />
                    </button>
                    <img src={lightbox} alt="" className="max-w-full max-h-full object-contain rounded-lg" />
                </div>
            )}
        </ScreenShell>
    );
}
